import { UploadDownloadService } from '@shared/services/upload-download.service';

/**
 * Abre un documento guardado en S3 de forma robusta.
 *
 * Todos los módulos hacían lo mismo a mano:
 *
 *     const file = await uploads.presign(key);
 *     if (file?.url) window.open(file.url, '_blank');
 *
 * …lo que fallaba en silencio por tres motivos distintos:
 *
 *  1. `window.open` se ejecuta DESPUÉS de un `await`, o sea fuera del gesto
 *     del usuario: el bloqueador de pop-ups lo cancela y "no pasa nada".
 *     Se resuelve disparando un `<a>` creado al vuelo.
 *  2. Si el registro guardó una URL http completa (dato heredado) en lugar de
 *     una key S3, `presign` no devuelve nada. Se detecta y se abre directo.
 *  3. Los errores se tragaban con `catch {}`, así que el usuario no distinguía
 *     entre "no hay documento" y "falló el servidor".
 *
 * Devuelve un mensaje de error legible, o `null` si el documento se abrió.
 */
export async function openS3File(
  uploads: UploadDownloadService,
  key: string | null | undefined,
): Promise<string | null> {
  if (!key) return 'El registro no tiene documento cargado.';

  const raw = String(key).trim();

  // Caso 1: ya es una URL completa (dato heredado). Se abre directo.
  if (/^https?:\/\//i.test(raw)) {
    triggerAnchor(raw);
    return null;
  }

  try {
    const file = await uploads.presign(raw);
    if (file?.url) {
      triggerAnchor(file.url);
      return null;
    }
    return 'No se pudo generar el enlace del documento. Verifica que el archivo siga existiendo.';
  } catch (err: any) {
    console.error('[openS3File] error', err);
    return `No se pudo abrir el documento: ${err?.message ?? err}`;
  }
}

/**
 * Crea un `<a target="_blank">`, lo dispara y lo elimina.
 * Se usa en lugar de `window.open` porque este último, tras un `await`,
 * suele quedar bloqueado por el bloqueador de pop-ups.
 */
function triggerAnchor(href: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
